import { useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { formatEur, type ServiceDTO } from '@wf/shared'
import { api } from '../api/client'
import { CustomerPicker } from '../customers/CustomerPicker'
import { VehiclePicker } from '../customers/VehiclePicker'
import { useCustomer } from '../customers/useCustomers'
import { newUuid } from '../offline/uuid'
import { DictationButton } from '../speech/DictationButton'
import { PRIO_OPTIONS } from './labels'
import { useCreateOrder } from './useOrders'

interface PickedItem {
  serviceId: number
  name: string
  priceCents: number
  vatRate: number
}

/**
 * Neuer Auftrag: bestehenden Kunden + sein Fahrzeug wählen, Priorität/Termin/Notiz setzen, Aufgaben frei
 * erfassen und Leistungen aus dem Preise-Katalog per Tipp-Vorschlag wählen — dann `POST /orders`.
 * Jede gewählte Leistung wandert als Abrechnungs-Position (items) UND als Aufgabe (tasks) in den Auftrag.
 * `client_uuid` ist über useRef stabil → ein Offline-Retry legt KEINEN Doppel-Auftrag an.
 */
export function NewOrderPage() {
  const navigate = useNavigate()
  const create = useCreateOrder()
  const [customerId, setCustomerId] = useState<number | null>(null)
  const [vehicleId, setVehicleId] = useState<number | null>(null)
  const customerDetail = useCustomer(customerId)
  const [prio, setPrio] = useState('mittel')
  const [note, setNote] = useState('')
  const [scheduledDate, setScheduledDate] = useState('')
  const [taskDraft, setTaskDraft] = useState('')
  const [tasks, setTasks] = useState<string[]>([])
  const [items, setItems] = useState<PickedItem[]>([])
  const [serviceQuery, setServiceQuery] = useState('')
  const [serviceFocused, setServiceFocused] = useState(false)
  const services = useQuery({ queryKey: ['services'], queryFn: api.listServices })
  const clientUuid = useRef(newUuid())

  const vehicles = customerId != null ? (customerDetail.data?.vehicles ?? []) : []

  // Vorschläge aus dem Preise-Katalog: bei Fokus schon ohne Eingabe (als Dropdown nutzbar), Tippen filtert;
  // bereits gewählte Leistungen ausblenden.
  const q = serviceQuery.trim().toLowerCase()
  const serviceMatches =
    serviceFocused || q.length >= 1
      ? (services.data?.services ?? [])
          .filter((s) => !items.some((i) => i.serviceId === s.id) && (q.length === 0 || s.name.toLowerCase().includes(q)))
          .slice(0, 8)
      : []

  function addTask() {
    const text = taskDraft.trim()
    if (!text) return
    setTasks((prev) => (prev.includes(text) ? prev : [...prev, text]))
    setTaskDraft('')
  }

  // Leistung übernehmen: als Position vormerken (Dedup nach serviceId) und Suchfeld leeren.
  function pickService(service: ServiceDTO) {
    setItems((prev) =>
      prev.some((item) => item.serviceId === service.id)
        ? prev
        : [...prev, { serviceId: service.id, name: service.name, priceCents: service.priceCents, vatRate: service.vatRate }],
    )
    setServiceQuery('')
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    // Jede gewählte Leistung erscheint zusätzlich als Aufgabe (Dedup gegen manuell erfasste Aufgaben).
    const taskTexts = [...tasks]
    for (const item of items) if (!taskTexts.includes(item.name)) taskTexts.push(item.name)
    create.mutate(
      {
        customerId,
        vehicleId,
        prio,
        note: note.trim() || null,
        scheduledDate: scheduledDate || null,
        tasks: taskTexts.map((text) => ({ text })),
        items: items.map((item) => ({ name: item.name, qty: 1, priceCents: item.priceCents, vatRate: item.vatRate, serviceId: item.serviceId })),
        client_uuid: clientUuid.current,
      },
      { onSuccess: (res) => navigate(`/orders/${res.order.id}`) },
    )
  }

  const errorMsg = create.error instanceof Error ? create.error.message : null

  return (
    <section className="wf-view">
      <div>
        <h2 className="viewtitle">Neuer Auftrag</h2>
        <p className="sub">Kunde &amp; Fahrzeug wählen, Aufgaben &amp; Leistungen erfassen</p>
      </div>
      <form onSubmit={submit} className="wf-settings">
        <CustomerPicker
          onChange={(id) => {
            setCustomerId(id)
            setVehicleId(null)
          }}
        />

        {customerId != null ? <VehiclePicker customerId={customerId} vehicles={vehicles} onChange={setVehicleId} /> : null}

        <div className="two">
          <label className="wf-field">
            <span className="wf-label">Priorität</span>
            <select className="wf-input" value={prio} onChange={(e) => setPrio(e.target.value)}>
              {PRIO_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="wf-field">
            <span className="wf-label">Termin</span>
            <input className="wf-input" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
          </label>
        </div>

        <div className="wf-field">
          <div className="wf-labelrow">
            <span className="wf-label">Notiz</span>
            <DictationButton onText={(text) => setNote((prev) => (prev ? `${prev} ${text}` : text))} label="Notiz diktieren" />
          </div>
          <textarea
            className="wf-input wf-textarea"
            rows={3}
            aria-label="Notiz"
            value={note}
            placeholder="z. B. Klappern vorne links seit gestern"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div>
          <span className="wf-label wf-sectiontitle">Aufgaben</span>
          {tasks.length > 0 ? (
            <ul className="wf-tasklist">
              {tasks.map((text, index) => (
                <li key={index} className="wf-line">
                  <span className="wf-line__main">{text}</span>
                  <button
                    type="button"
                    className="wf-iconbtn"
                    aria-label={`Aufgabe entfernen: ${text}`}
                    onClick={() => setTasks((prev) => prev.filter((_, other) => other !== index))}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="wf-task-add">
            <input
              className="wf-input"
              placeholder="Aufgabe hinzufügen …"
              aria-label="Aufgabe"
              value={taskDraft}
              onChange={(e) => setTaskDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addTask()
                }
              }}
            />
            <button type="button" className="wf-btn wf-btn--ghost" onClick={addTask} disabled={!taskDraft.trim()}>
              Hinzufügen
            </button>
          </div>
        </div>

        <div>
          <span className="wf-label wf-sectiontitle">Leistungen</span>
          {items.length > 0 ? (
            <ul className="wf-tasklist">
              {items.map((item) => (
                <li key={item.serviceId} className="wf-line">
                  <span className="wf-line__main">
                    {item.name} · {formatEur(item.priceCents)}
                  </span>
                  <button
                    type="button"
                    className="wf-iconbtn"
                    aria-label={`Leistung entfernen: ${item.name}`}
                    onClick={() => setItems((prev) => prev.filter((i) => i.serviceId !== item.serviceId))}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <input
            className="wf-input"
            placeholder="Leistung aus Preisliste suchen …"
            aria-label="Leistung suchen"
            value={serviceQuery}
            onFocus={() => setServiceFocused(true)}
            onBlur={() => setTimeout(() => setServiceFocused(false), 120)}
            onChange={(e) => setServiceQuery(e.target.value)}
          />
          {serviceMatches.length > 0 ? (
            <div className="wf-linelist">
              {serviceMatches.map((service) => (
                <div key={service.id} className="wf-line">
                  <span className="wf-line__main">
                    {service.name} · {formatEur(service.priceCents)}
                  </span>
                  <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => pickService(service)}>
                    Übernehmen
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {errorMsg ? (
          <p className="error" role="alert">
            {errorMsg}
          </p>
        ) : null}
        <div className="wf-listfoot">
          <button type="button" className="wf-btn wf-btn--ghost" onClick={() => navigate('/orders')}>
            Abbrechen
          </button>
          <button type="submit" className="wf-btn" disabled={create.isPending}>
            {create.isPending ? 'Anlegen…' : 'Auftrag anlegen'}
          </button>
        </div>
      </form>
    </section>
  )
}
